import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { useWindowWidth } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { Popup, usePortalNestProvider } from '@elfsight/widget-modal';
import { getBreakpointsValues, WidgetBackground } from '@elfsight/widget-ui';
import { useWidgetRect, useWidgetRectRef } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { flushSync } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { layoutSettingsOptions } from '../constants';
import { meta } from '../meta';
import { Confetti } from './confetti';
import { Content } from './content/content';
import { EMAIL_ID } from './content/form/constants';
import {
  useSpinningWheelFields,
  useSyncRewardFields
} from './content/form/use-spinning-wheel-fields';
import { useFormController } from './form-controller-context';
import { ReopenButton } from './reopen-button';
import { useApplyTriggers } from './triggered/use-apply-triggers';
import { useTriggers } from './triggered/use-triggers';
import { useStorageReward } from './use-current-reward';
import { Wheel, type WheelHandle, type WheelRewardType } from './wheel';

const SESSION_STORAGE_KEY = 'elfsight-spinning-wheel-';
export function Widget() {
  const settings = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const {
    background,
    reopenButton,
    editMode,
    emailRequiredToSpin,
    _editingReward,
    widgetId,
    rewards,
    renderThumbnailMode,
    headerTitle,
    headerCaption,
    logo,
    footerText,
    accentColor,
    textColor,
    reopenButtonText,
    reopenButtonIcon,
    reopenButtonPosition,
    showPopup,
    googleAnalytics
  } = settings;

  const shouldStoreWinningReward =
    process.env.NODE_ENV !== 'development' || !editMode;

  const widgetSessionStorageKey = SESSION_STORAGE_KEY + widgetId;
  const getSessionKeyValue = () =>
    window.sessionStorage.getItem(widgetSessionStorageKey);
  const removeSessionKeyValue = () =>
    window.sessionStorage.removeItem(widgetSessionStorageKey);

  const { reward: storageReward, setStorageRewardId } = useStorageReward();
  const currentReward = shouldStoreWinningReward ? storageReward : undefined;
  const [winningReward, setWinningReward] = useState<
    WheelRewardType | undefined
  >(currentReward);

  const [popup, setPopup] = useState(!!editMode || !!renderThumbnailMode);
  const [confetti, setConfetti] = useState(false);

  const { submitCallbackRef, focusCallbackRef } = useFormController();

  const { sync: syncRewardFields } = useSyncRewardFields();
  useSpinningWheelFields();

  const { fields } = useFormSelector();
  const isSpinAgain = winningReward?.type === 'spinAgain';
  const isEmailValid = fields?.[EMAIL_ID]?.valid;
  const emailValue = fields?.[EMAIL_ID]?.value;

  const isWheelDisabled =
    !isSpinAgain && emailRequiredToSpin && (!isEmailValid || !emailValue);

  const portal = usePortalNestProvider();
  const widgetRectRef = useWidgetRectRef();
  const contentRect = useWidgetRect();
  const wheelHandleRef = useRef<WheelHandle | null>(null);
  const prevPopupRef = useRef(false);
  const formStartedSentRef = useRef(false);

  const windowWidth = useWindowWidth();
  const isVertical = windowWidth < 699;

  const layoutSettings = getBreakpointsValues(
    windowWidth,
    layoutSettingsOptions
  );

  const trigger = useTriggers();
  const activateTrigger = useCallback(() => {
    const sessionStorageValue = getSessionKeyValue();
    if (!winningReward && sessionStorageValue === 'shownAndSpun') {
      removeSessionKeyValue();
    }
    if (!sessionStorageValue) openPopupHandler();
  }, []);

  useApplyTriggers({ triggers: trigger, onActivate: activateTrigger });

  const runConfettiHandler = () => {
    setConfetti(true);
    setTimeout(() => {
      setConfetti(false);
    }, 2500);
  };

  function handleWheelSpinEnd(winningReward: WheelRewardType) {
    if (shouldStoreWinningReward) {
      setStorageRewardId(winningReward.id);
    }
    setWinningReward(winningReward);
    runConfettiHandler();
    // TODO: temporary solution using flushSync due to automatic batching - find better solution
    flushSync(() => syncRewardFields(winningReward));
    window.sessionStorage.setItem(widgetSessionStorageKey, 'shownAndSpun');

    // Google Analytics: Spin Completed event
    sendGoogleAnalyticsEvents({
      eventType: 'spinCompleted',
      googleAnalyticsEvents: googleAnalytics,
      valueMapping: {
        widgetId,
        prizeName: winningReward.label,
        prizeId: winningReward.id
      }
    });

    if (submitCallbackRef.current) {
      submitCallbackRef.current();

      // Google Analytics: Form Submitted event
      sendGoogleAnalyticsEvents({
        eventType: 'formSubmitted',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId
        }
      });
    }
  }

  const spinHandler = () => {
    wheelHandleRef.current?.spin();

    // Google Analytics: Spin Started event
    sendGoogleAnalyticsEvents({
      eventType: 'spinStarted',
      googleAnalyticsEvents: googleAnalytics,
      valueMapping: {
        widgetId
      }
    });
  };

  const closePopupHandler = () => {
    setPopup(false);
    if (editMode) {
      if (!_editingReward) {
        setWinningReward(undefined);
      }
    }
  };

  useEffect(() => {
    if (editMode) {
      if (!reopenButton && !popup) {
        setTimeout(() => {
          openPopupHandler();
        }, 1000);
      }
    }
  }, [popup]);

  const openPopupHandler = () => {
    setPopup(true);
    if (winningReward) {
      runConfettiHandler();
    }
    if (!getSessionKeyValue()) {
      window.sessionStorage.setItem(widgetSessionStorageKey, 'shown');
    }
  };

  // Google Analytics: Wheel Opened event
  useEffect(() => {
    const prevPopup = prevPopupRef.current;

    if (!prevPopup && popup) {
      sendGoogleAnalyticsEvents({
        eventType: 'wheelOpened',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId
        }
      });
    }

    prevPopupRef.current = popup;
  }, [popup, googleAnalytics, widgetId]);

  // Google Analytics: Form Started event
  useEffect(() => {
    if (emailValue && !formStartedSentRef.current) {
      sendGoogleAnalyticsEvents({
        eventType: 'formStarted',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId
        }
      });
      formStartedSentRef.current = true;
    }
  }, [emailValue, googleAnalytics, widgetId]);

  useEffect(() => {
    if (winningReward && !editMode) {
      runConfettiHandler();
    }
  }, []);

  useEffect(() => {
    if (editMode) {
      setWinningReward(_editingReward);
      setPopup(true);
    }
  }, [_editingReward]);

  useEffect(() => {
    if (editMode && reopenButton) {
      setPopup(false);
    }
  }, [reopenButton, reopenButtonText, reopenButtonIcon, reopenButtonPosition]);

  useEffect(() => {
    if (editMode) {
      setPopup(true);
    }
  }, [
    headerTitle,
    headerCaption,
    logo,
    footerText,
    emailRequiredToSpin,
    background,
    accentColor,
    textColor,
    reopenButton,
    showPopup
  ]);

  return (
    <>
      <Popup
        ariaLabel={headerTitle}
        portalContainerElement={portal as Element}
        width={1200}
        show={popup}
        closeControlColor="rgb(17, 17, 17)"
        onCloseRequest={closePopupHandler}
        margin={layoutSettings.sidePaddings}
        fullscreen={layoutSettings.sidePaddings === 0}
        withBackdrop={!renderThumbnailMode}
        disableFocusOnOpen={editMode}
        closeOnOutsideClick
        fixed
        withCloseControl
        closeOnEscape
        disableScroll
      >
        <CSSTransition
          in={confetti}
          timeout={2000}
          classNames="fade"
          unmountOnExit
        >
          <Confetti
            size={[contentRect?.width || 0, contentRect?.height || 0]}
          />
        </CSSTransition>
        <Deactivation2
          childrenRef={widgetRectRef}
          appAlias={meta.appAlias}
          {...settings}
        >
          <WidgetBackground
            {...background}
            borderRadius={layoutSettings.noBorderRadius ? 0 : 20}
            verticalPadding={layoutSettings.paddings}
            horizontalPadding={layoutSettings.paddings}
            style={{
              overflow: 'hidden',
              minHeight: '100%',
              boxShadow: renderThumbnailMode ? '0 0 30px rgba(0,0,0,0.1)' : ''
            }}
            contentStyle={{
              position: 'relative'
            }}
          >
            <Container
              _isVertical={isVertical}
              _gap={layoutSettings.spinAndContentSpacing}
            >
              <WheelContainer
                _isVertical={isVertical}
                _width={layoutSettings.wheelContainerWidth}
              >
                {rewards.length > 0 ? (
                  <Wheel
                    ref={wheelHandleRef}
                    orientation={isVertical ? 'vertical' : 'horizontal'}
                    selectedWinningRewardId={winningReward?.id}
                    onSpinEnd={handleWheelSpinEnd}
                    disabled={isWheelDisabled}
                    onInteractWhenDisabled={() => {
                      focusCallbackRef?.current?.();
                    }}
                  />
                ) : (
                  <NoReward _textColor={textColor}>
                    No prizes added yet. Add at least one prize to display the
                    wheel.
                  </NoReward>
                )}
              </WheelContainer>
              <StyledContent
                winningReward={winningReward}
                spin={spinHandler}
                closePopup={closePopupHandler}
                _width={layoutSettings.contentContainerWidth}
              />
            </Container>
            {!layoutSettings.sidePaddings && (
              <FreeLink
                {...freeLinkSettings}
                appAlias={meta.appAlias}
                appName={meta.appName}
                style={{
                  link: {
                    marginTop: '40px'
                  }
                }}
              />
            )}
          </WidgetBackground>
        </Deactivation2>
        {!!layoutSettings.sidePaddings && (
          <FreeLink
            {...freeLinkSettings}
            appAlias={meta.appAlias}
            appName={meta.appName}
            style={{
              link: {
                marginTop: '20px',
                marginBottom: '40px'
              }
            }}
          />
        )}
      </Popup>
      <ReopenButton onClick={openPopupHandler} />
    </>
  );
}

const NoReward = styled.div<{ _textColor: string }>`
  color: ${({ _textColor }) => _textColor};
  width: 100%;
  text-align: center;
  font-size: 15px;
  opacity: 0.5;
`;

const WheelContainer = styled.div<{
  _width: number | string;
  _isVertical?: boolean;
}>`
  width: ${({ _width }) => _width};
  position: relative;
`;

const StyledContent = styled(Content)<{ _width: number | string }>`
  width: ${({ _width }) => _width};
`;

const Container = styled.div<{ _isVertical: boolean; _gap: number }>`
  display: flex;
  align-items: center;
  gap: ${({ _gap }) => _gap}px;
  flex-direction: ${({ _isVertical }) => (_isVertical ? 'column' : 'row')};
`;
